"use client";
import { useTranslations } from "next-intl"

import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronRight, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { discoverRowCtaButtonClassName, discoverRowCtaChevronClassName } from "@/lib/discover-row-cta-button";
import { ItemCard } from "@/components/search/item-card";
import { ItemCardSkeletonGrid } from "@/components/search/item-card-skeleton";
import { useMediaQuery } from "@/hooks/use-media-query";

// 1) On passe image_urls à string[] (au lieu de string)
export interface Listing {
  id: string;
  title: string;
  description: string;
  price: string;
  currency?: string | null;
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: legacy `location` is no longer
  // surfaced by listing fetches (search route still emits it; profile/feed
  // route now emits structured `shipFromAddress`). Relaxed to optional so
  // `ListingResult[]` (from `@kraahl/contract`) is assignable here. The
  // structured trio is not consumed inside ListingResults today; ItemCard
  // takes it via its own props if/when needed.
  location?: string;
  image_urls: string[];         // ← ici
  created_at: string;
  condition: string;
  grade: string;
  quantity?: number;            // ← quantité disponible (0 = sold out)
  username?: string;
  avatar_url?: string | null;
  scheduled_publish_at?: string | null;
  listing_format?: string | null;
  user_id?: string;
  type?: "standard" | "preorder" | null;
  // Phase 3 (2026-06-29): `accepts_offers` dropped — derive via
  // `isOfferEnabledFormat(listing_format)` from `@kraahl/contract`.
  offers_pending_count?: number | null;
}

export interface ListingResultsProps {
  listings: Listing[];
  searchQuery: string;
  /** Nombre max d'items à afficher */
  limit?: number;
  /** Afficher le lien "See more" */
  showSeeMore?: boolean;
  /** Utiliser l'infinite scroll au lieu des boutons */
  useInfiniteScroll?: boolean;
  /** Cacher le titre interne (h2) */
  showHeader?: boolean;
  /** Action au clic sur une annonce (optionnel, no-op par défaut) */
  onListingClickAction?: (listing: Listing) => void;
  /** Action lors du toggle "save" (optionnel) */
  onSaveToggleAction?: (itemId: string, next: boolean) => void;
  /** Action lors du toggle "follow" (optionnel) */
  onFollowChangeAction?: (itemId: string, next: boolean) => void;
  sellerBanned?: boolean;
  currentUserId?: string;
  /** Fired when the infinite-scroll sentinel intersects. Caller can fetch
   *  more items from a server and prepend/append them to the `listings`
   *  prop. Independent of the local client-side slice incrementing. */
  onEndReached?: () => void;
  /** If false, hide the sentinel even when there are still locally-buffered
   *  items. Used by server-paginated callers to stop scroll prompting after
   *  the last server page lands. */
  hasMoreFromServer?: boolean;
  isLoadingMore?: boolean;
}

export function ListingResults({
  listings,
  searchQuery,
  limit = Infinity,
  showSeeMore = false,
  useInfiniteScroll = false,
  showHeader = true,
  onListingClickAction = () => {
},
  onSaveToggleAction = () => {},
  onFollowChangeAction = () => {},
  sellerBanned = false,
  currentUserId,
  onEndReached,
  hasMoreFromServer,
  isLoadingMore = false,
}: ListingResultsProps) {
  const t = useTranslations("common");
  const isSm = useMediaQuery("(min-width: 640px)");
  const isLg = useMediaQuery("(min-width: 1024px)");
  const columns = isLg ? 4 : isSm ? 3 : 2;
  // Server-paginated mode = caller has an external load-more pipeline
  // feeding `listings` from a backend. In that mode we MUST NOT do
  // client-side slicing: when the caller appends a new page, our local
  // currentLimit would otherwise be reset by the listings.length effect
  // and the visible row count would collapse back to `limit` mid-scroll
  // — the "jerky scroll on load" symptom.
  const isServerPaginated = Boolean(onEndReached);
  const [currentLimit, setCurrentLimit] = useState(() => Math.min(limit, listings.length || limit));
  const [showLoadMoreSkeleton, setShowLoadMoreSkeleton] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const visibleListings = isServerPaginated ? listings : listings.slice(0, currentLimit);
  const hasMoreLocal = !isServerPaginated && listings.length > currentLimit;
  const shouldRenderLoadMoreSkeleton = isLoadingMore || showLoadMoreSkeleton;
  const trailingCount = visibleListings.length % columns;
  const fillerCount = trailingCount === 0 ? 0 : columns - trailingCount;
  // Keep the sentinel mounted while EITHER more locally-buffered items
  // remain OR the server can still hand us more. Without the latter the
  // sentinel hides the moment the local slice is exhausted and onEndReached
  // never fires for the next server page.
  const showSentinel =
    useInfiniteScroll && (hasMoreLocal || hasMoreFromServer === true);

  const loadMore = useCallback(() => {
    if (isServerPaginated) return; // server is the pagination authority
    if (hasMoreLocal && useInfiniteScroll) {
      setCurrentLimit((prev) => {
        if (limit === Infinity) {
          return Math.min(listings.length, prev + 16);
        }
        return Math.min(listings.length, prev + limit);
      });
    }
  }, [hasMoreLocal, limit, useInfiniteScroll, listings.length, isServerPaginated]);

  const handleSeeMore = () => {
    setCurrentLimit((prev: number) => {
      if (limit === Infinity) {
        return Math.min(listings.length, prev + 16);
      }
      return Math.min(listings.length, prev + limit);
    });
  };

  useEffect(() => {
    // Only re-clamp in client-side-only mode. In server-paginated mode the
    // visible set equals `listings` directly, so resetting currentLimit on
    // each append would visually collapse already-rendered rows and cause
    // the scroll position to jump.
    if (isServerPaginated) return;
    setCurrentLimit(Math.min(limit, listings.length || limit));
  }, [limit, listings.length, isServerPaginated]);

  useEffect(() => {
    if (isLoadingMore) {
      setShowLoadMoreSkeleton(true);
      return;
    }
    if (!showLoadMoreSkeleton) {
      return;
    }
    const timeoutId = window.setTimeout(() => setShowLoadMoreSkeleton(false), 220);
    return () => window.clearTimeout(timeoutId);
  }, [isLoadingMore, showLoadMoreSkeleton]);

  // Infinite scroll effect
  useEffect(() => {
    if (!useInfiniteScroll) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // 1. Reveal the next slice of already-loaded items (no-op if
          //    we're at the end of the local buffer).
          loadMore();
          // 2. Ask the caller for more items from the server. Caller is
          //    responsible for de-duping concurrent fires (the hook
          //    guards with an in-flight ref).
          onEndReached?.();
        }
      },
      { rootMargin: "200px" }
    );
    observerRef.current.observe(sentinel);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [loadMore, useInfiniteScroll, onEndReached]);

  // Re-arm after a load settles. IntersectionObserver only fires on an
  // intersection *transition*, so if the sentinel is still in view when the
  // previous page lands (short page, or already at the very bottom) it never
  // re-fires — the user has to scroll up and back down to trigger the next
  // fetch. Once loading finishes, if the sentinel is still within the viewport
  // (+200px preload margin) and the server has more, ask for it directly.
  useEffect(() => {
    if (!useInfiniteScroll || isLoadingMore || hasMoreFromServer !== true) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const rect = sentinel.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 200) {
      onEndReached?.();
    }
  }, [useInfiniteScroll, isLoadingMore, hasMoreFromServer, listings.length, onEndReached]);

  if (listings.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-900 mb-4">
          <ShoppingBag className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-gray-500 dark:text-gray-400">
          No listings found.
        </p>
      </div>
    );
  }

  return (
    <div className="listing-results-container">
      {showHeader && (
        <h2 className="text-lg font-semibold mb-1 text-gray-800 dark:text-gray-200 flex items-center">
          <ShoppingBag className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
          Listings
        </h2>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-x-4 md:gap-x-5 gap-y-4">
        {visibleListings.map((l) => (
          <div key={l.id} className="h-auto min-w-0">
            <ItemCard
              listingId={l.id}
              title={l.title}
              price={l.price}
              currency={l.currency ?? null}
              image_urls={l.image_urls}
              stock={l.quantity}
              scheduledPublishAt={l.scheduled_publish_at}
              listingFormat={l.listing_format ?? null}
              listingType={l.type ?? null}
              offersPendingCount={l.offers_pending_count ?? null}
              sellerBanned={sellerBanned}
              currentUserId={currentUserId}
              sellerId={l.user_id}
              sellerName={l.username}
              sellerAvatarUrl={l.avatar_url}
              condition={l.condition}
              description={l.description}
              onClick={() => onListingClickAction(l)}
            />
          </div>
        ))}
        {shouldRenderLoadMoreSkeleton && fillerCount > 0 ? (
          <ItemCardSkeletonGrid count={fillerCount} keyPrefix="listing-results-filler" inline />
        ) : null}
        {shouldRenderLoadMoreSkeleton ? (
          <ItemCardSkeletonGrid count={columns} keyPrefix="listing-results-loading" inline />
        ) : null}
      </div>

      {/* Infinite scroll sentinel */}
      {showSentinel && <div ref={sentinelRef} className="h-1" />}

      {/* See more button */}
      {showSeeMore && !useInfiniteScroll && hasMoreLocal && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSeeMore}
            className={`mt-4 ${discoverRowCtaButtonClassName}`}
          >
            <span>{t("seeMoreListings")}</span>
            <ChevronRight className={discoverRowCtaChevronClassName} aria-hidden />
          </Button>
        </div>
      )}
    </div>
  );
}
